/**
 * Assisted DR and EP entry. Tap the start position, type the course steered,
 * the speed through the water, and the time run, and for an EP the tidal set
 * and rate (prefilled from the scenario) and optional leeway. The app draws the
 * construction: water track and DR, and for an EP the tidal vector, the EP, and
 * the ground track.
 *
 * This is the assisted way of working: enter the numbers, let the app draw the
 * result, good for checking work or demonstrating a concept. The fully manual
 * single-tap DR and EP (using the boat's current heading and speed) stay on the
 * palette when the manual toggle is set.
 */
import { useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore } from '@/store';
import { useEnvStore } from '@/store/environment';
import { useObjectsStore, nextObjectId } from '@/store/objects';
import { buildDR, buildEP } from '@/chartwork';
import { sampleTide } from '@/environment';
import { formatPosition } from '@/nav';
import { Sheet } from './Sheet';

type Kind = 'dr' | 'ep';

export function TracksSheet() {
  const pendingMark = useShellStore((s) => s.pendingMark);
  const armFixPick = useShellStore((s) => s.armFixPick);
  const setPendingMark = useShellStore((s) => s.setPendingMark);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const tide = useEnvStore((s) => s.tide);
  const clockH = useEnvStore((s) => s.clockH);
  const streamNow = sampleTide(tide, clockH);

  const [kind, setKind] = useState<Kind>('ep');
  const [startPt, setStartPt] = useState<[number, number] | null>(null);
  const [course, setCourse] = useState(() => String(Math.round(ownship.heading || 90)));
  const [speed, setSpeed] = useState(() => String(ownship.stw || ownship.sog || 6));
  const [hours, setHours] = useState('1');
  const [set, setSet] = useState(() => String(Math.round(streamNow.setDeg)));
  const [rate, setRate] = useState(() => streamNow.rateKn.toFixed(1));
  const [leeway, setLeeway] = useState('');

  function reset() {
    setStartPt(null);
    setPendingMark(null);
  }

  function compute() {
    const c = Number.parseFloat(course);
    const sp = Number.parseFloat(speed);
    const h = Number.parseFloat(hours);
    const st = Number.parseFloat(set);
    const rt = Number.parseFloat(rate);
    const lw = leeway.trim() === '' ? 0 : Number.parseFloat(leeway);
    const needed = kind === 'ep' ? [c, sp, h, st, rt, lw] : [c, sp, h];
    if (!startPt || needed.some(Number.isNaN)) {
      showToast('Pick the start and check the figures');
      return;
    }
    const time = { iso: new Date().toISOString() };
    if (kind === 'dr') {
      const built = buildDR({
        start: startPt,
        headingDeg: c,
        speedKnots: sp,
        hours: h,
        time,
        newId: nextObjectId,
      });
      useObjectsStore.getState().putObjects([built.start, built.waterTrack, built.dr]);
      reset();
      closeSheet();
      showToast(`DR plotted at ${formatPosition(built.dr.position, { decimals: 1 })}`);
    } else {
      const built = buildEP({
        start: startPt,
        headingDeg: c,
        speedKnots: sp,
        hours: h,
        tidalSet: st,
        tidalRate: rt,
        leewayDeg: lw,
        time,
        newId: nextObjectId,
      });
      useObjectsStore
        .getState()
        .putObjects([
          built.start,
          built.waterTrack,
          built.dr,
          built.tidalVector,
          built.ep,
          built.groundTrack,
          // The uncertainty ellipse, so a typed EP shows the same honest doubt as
          // a tapped one (the tap tool plots built.ellipse too). Without it a
          // by-the-numbers EP looked more certain than it is.
          built.ellipse,
        ]);
      reset();
      closeSheet();
      showToast(`EP plotted at ${formatPosition(built.ep.position, { decimals: 1 })}`);
    }
  }

  return (
    <Sheet
      name="tracks"
      title="DR / EP by the numbers"
      sub="Tap the start, enter the course, speed, and time, and we draw the construction. For an EP the tide is applied from the DR; it comes from the scenario, adjust if you like."
    >
      <div className="acp-seg">
        <button type="button" className={kind === 'dr' ? 'on' : ''} onClick={() => setKind('dr')}>
          DR
        </button>
        <button type="button" className={kind === 'ep' ? 'on' : ''} onClick={() => setKind('ep')}>
          EP
        </button>
      </div>

      <div className="acp-kv">
        <span className="k">Start position</span>
        <span className="v">{startPt ? 'set' : '—'}</span>
      </div>

      {!startPt ? (
        pendingMark ? (
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn primary"
              onClick={() => {
                setStartPt(pendingMark.position);
                setPendingMark(null);
              }}
            >
              Use as start
            </button>
          </div>
        ) : (
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn primary"
              onClick={() => {
                armFixPick('tracks');
                showToast('Tap the start position');
              }}
            >
              Pick start
            </button>
          </div>
        )
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Field label="Course °T" value={course} onChange={setCourse} />
            <Field label="Speed kn" value={speed} onChange={setSpeed} />
            <Field label="Hours" value={hours} onChange={setHours} />
          </div>
          {kind === 'ep' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <Field label="Tide set °" value={set} onChange={setSet} />
              <Field label="Tide rate kn" value={rate} onChange={setRate} />
              <Field label="Leeway °" value={leeway} onChange={setLeeway} />
            </div>
          )}
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={reset}>
              Start over
            </button>
            <button type="button" className="acp-btn primary" onClick={compute}>
              {kind === 'dr' ? 'Plot the DR' : 'Plot the EP'}
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ flex: 1, fontSize: 11, color: 'var(--ink-soft)' }}>
      {label}
      <input
        className="acp-note-input"
        style={{ marginTop: 2 }}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
